import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, TextField, CircularProgress, Grid,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { Receipt, Print, Download } from '@mui/icons-material';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import api from '../../api/axios';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

export default function GSTReportPage() {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
  });
  const [to, setTo] = useState(new Date().toISOString().split('T')[0]);
  const [groupBy, setGroupBy] = useState('month');
  const printRef = useRef();
  const handlePrint = useReactToPrint({ contentRef: printRef });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/reports/gst', { params: { from, to, group_by: groupBy } });
      setRows(data.data.rows || []);
      setTotals(data.data.totals);
    } finally { setLoading(false); }
  }, [from, to, groupBy]);

  useEffect(() => { load(); }, [load]);

  const taxRate = totals && totals.total_revenue > 0
    ? ((totals.total_tax / totals.total_revenue) * 100).toFixed(2)
    : '0.00';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Receipt color="primary" />
          <Typography variant="h5" fontWeight={700}>GST / Tax Report</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Print />} onClick={handlePrint}>Print</Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pb: '16px !important', alignItems: 'center' }}>
          <TextField label="From" type="date" size="small" value={from}
            onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="To" type="date" size="small" value={to}
            onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <ToggleButtonGroup size="small" value={groupBy} exclusive onChange={(_, v) => v && setGroupBy(v)}>
            <ToggleButton value="day">Daily</ToggleButton>
            <ToggleButton value="month">Monthly</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" onClick={load} disabled={loading}>
            {loading ? <CircularProgress size={18} /> : 'Generate'}
          </Button>
        </CardContent>
      </Card>

      {/* KPI cards */}
      {totals && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Invoices', value: totals.total_invoices, color: 'info.main' },
            { label: 'Gross Revenue', value: `₨ ${fmt(totals.total_revenue)}`, color: 'primary.main' },
            { label: 'Total Discounts', value: `₨ ${fmt(totals.total_discounts)}`, color: 'warning.main' },
            { label: 'GST Collected', value: `₨ ${fmt(totals.total_tax)}`, color: 'success.main' },
            { label: 'Effective Tax Rate', value: `${taxRate}%`, color: 'secondary.main' },
          ].map((kpi, i) => (
            <Grid item xs={6} sm={4} md={2.4} key={i}>
              <Card>
                <CardContent sx={{ pb: '16px !important' }}>
                  <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                  <Typography variant="h6" fontWeight={700} color={kpi.color}>{kpi.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Printable area */}
      <Box ref={printRef}>
        <style>{`@media print { @page { size: A4; margin: 15mm; } }`}</style>
        <Box sx={{ display: { screen: 'none', print: 'block' }, mb: 3 }}>
          <Typography variant="h5" fontWeight={700}>GST / Tax Report</Typography>
          <Typography variant="body2">Period: {from} to {to}</Typography>
        </Box>

        <Card>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Invoices</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Subtotal</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Discounts</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Taxable Amount</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>GST Collected</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Total (incl. GST)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No data for selected range</TableCell></TableRow>
              )}
              {rows.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell><Typography fontFamily="monospace" fontWeight={600}>{r.period}</Typography></TableCell>
                  <TableCell align="right">{r.invoices}</TableCell>
                  <TableCell align="right">₨ {fmt(r.subtotal)}</TableCell>
                  <TableCell align="right">
                    <Typography color="warning.main">₨ {fmt(r.discounts)}</Typography>
                  </TableCell>
                  <TableCell align="right">₨ {fmt((+r.subtotal || 0) - (+r.discounts || 0))}</TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={700} color="success.main">₨ {fmt(r.tax_collected)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={700}>₨ {fmt(r.total_with_tax)}</Typography>
                  </TableCell>
                </TableRow>
              ))}
              {/* Totals row */}
              {totals && rows.length > 0 && (
                <TableRow sx={{ bgcolor: 'primary.50', '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'primary.main' } }}>
                  <TableCell>TOTAL</TableCell>
                  <TableCell align="right">{totals.total_invoices}</TableCell>
                  <TableCell align="right">₨ {fmt(+totals.total_revenue - +totals.total_tax)}</TableCell>
                  <TableCell align="right">₨ {fmt(totals.total_discounts)}</TableCell>
                  <TableCell align="right">₨ {fmt((+totals.total_revenue - +totals.total_tax) - +totals.total_discounts)}</TableCell>
                  <TableCell align="right" sx={{ color: 'success.main' }}>₨ {fmt(totals.total_tax)}</TableCell>
                  <TableCell align="right">₨ {fmt(totals.total_revenue)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>

        {/* Footer note */}
        {totals && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              This report shows GST/Sales Tax collected from customers on all completed sales.
              Ensure FBR returns are filed as per applicable tax slabs.
              Report generated: {new Date().toLocaleString('en-PK')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
